/**
 * Spendings Dual-Write Test
 *
 * Tests that spending operations in Firestore are properly synced to Supabase.
 *
 * Test variations:
 * S1: Create spending
 * S2: Delete spending
 */

import {
  saveNewSpending,
  deleteSpending,
} from '../global/Firebase/firebase';
import { api } from '../global/API/apiClient';
import { getDefaultStore } from 'jotai';
import { organizationAtom, storesDictAtom, dbAtom, paymentMethodsDictAtom } from '../global/AtomsProvider';

// Test variation definitions
export const SPENDING_TEST_VARIATIONS = [
  { id: 'S1', category: 'create', name: 'Create spending' },
  { id: 'S2', category: 'delete', name: 'Delete spending' },
];

// Get test categories for UI grouping
export function getSpendingTestCategories() {
  return [
    { id: 'create', name: 'Create Spending', variations: ['S1'] },
    { id: 'delete', name: 'Delete Spending', variations: ['S2'] },
  ];
}

// Helper to normalize values for comparison
function normalizeValue(val) {
  if (val === undefined || val === null) return null;
  if (typeof val === 'number') return val;
  if (typeof val === 'string') {
    const num = parseFloat(val);
    if (!isNaN(num)) return num;
    return val.trim();
  }
  return val;
}

// Helper to compare values with tolerance for floating point
function valuesMatch(a, b, tolerance = 0.01) {
  const normA = normalizeValue(a);
  const normB = normalizeValue(b);

  if (normA === normB) return true;
  if (normA === null || normB === null) return normA === normB;

  // Numeric comparison with tolerance
  if (typeof normA === 'number' && typeof normB === 'number') {
    return Math.abs(normA - normB) < tolerance;
  }

  return normA === normB;
}

/**
 * Get test data based on atoms
 */
export function getSpendingTestData() {
  const jotaiStore = getDefaultStore();
  const organization = jotaiStore.get(organizationAtom);
  const storesDict = jotaiStore.get(storesDictAtom);
  const db = jotaiStore.get(dbAtom);
  const paymentMethodsDict = jotaiStore.get(paymentMethodsDictAtom);

  if (!organization || !db) {
    return null;
  }

  // Get first store
  const storeEntries = Object.entries(storesDict || {});
  if (storeEntries.length < 1) {
    return null;
  }

  const store = { id: storeEntries[0][0], ...storeEntries[0][1] };

  // Get first payment method for spending test
  const paymentMethodEntries = Object.entries(paymentMethodsDict || {});
  const paymentSource = paymentMethodEntries.length > 0
    ? { id: paymentMethodEntries[0][0], ...paymentMethodEntries[0][1] }
    : { id: 'cash', name: 'Cash' };

  return {
    organization,
    store,
    db,
    organizationId: organization.id,
    paymentSource,
  };
}

/**
 * Compare spending via backend API
 */
async function compareSpendingViaApi(spendingId, spendingData, log) {
  try {
    const response = await api.get(`/spendings/compare/${spendingId}`);
    const apiResult = response.data;

    if (!apiResult.exists) {
      return {
        spendingId,
        foundInSupabase: false,
        matches: false,
        differences: [{
          field: 'spending',
          supabaseValue: null,
          firestoreValue: 'exists',
          severity: 'critical',
        }],
        warnings: [],
      };
    }

    const supabaseData = apiResult.supabaseData;
    const differences = [];

    // Compare key fields
    const fieldsToCompare = [
      { fs: 'amount', sb: 'amount' },
    ];

    for (const { fs, sb } of fieldsToCompare) {
      const fsVal = normalizeValue(spendingData?.[fs]);
      const sbVal = normalizeValue(supabaseData?.[sb]);

      if (!valuesMatch(fsVal, sbVal)) {
        differences.push({
          field: sb,
          supabaseValue: sbVal,
          firestoreValue: fsVal,
          severity: 'critical',
        });
      }
    }

    // Compare name (Firestore uses 'details', Supabase uses 'name')
    const fsName = normalizeValue(spendingData?.details);
    const sbName = normalizeValue(supabaseData?.name);
    if (fsName && sbName && fsName !== sbName) {
      differences.push({
        field: 'name',
        supabaseValue: sbName,
        firestoreValue: fsName,
        severity: 'warning',
      });
    }

    return {
      spendingId,
      foundInSupabase: true,
      matches: differences.filter(d => d.severity === 'critical').length === 0,
      differences,
      warnings: differences.filter(d => d.severity === 'warning').map(d => d.field),
      supabaseData,
    };
  } catch (error) {
    log(`API comparison error: ${error.message}`);
    return {
      spendingId,
      foundInSupabase: false,
      matches: false,
      differences: [{
        field: 'api_error',
        supabaseValue: null,
        firestoreValue: error.message,
        severity: 'critical',
      }],
      warnings: [`API error: ${error.message}`],
    };
  }
}

/**
 * Check if spending was deleted from Supabase
 */
async function checkSpendingDeleted(spendingId, maxRetries = 3) {
  for (let i = 0; i < maxRetries; i++) {
    try {
      const response = await api.get(`/spendings/compare/${spendingId}`);
      if (!response.data.exists) {
        return true;
      }
      // Wait before retry
      await new Promise(resolve => setTimeout(resolve, 2000));
    } catch (error) {
      // 404 means deleted
      if (error.response?.status === 404) {
        return true;
      }
    }
  }
  return false;
}

/**
 * Run a single spending test
 */
async function runSingleTest(variationId, testData, log) {
  const { db, store, organizationId, paymentSource } = testData;

  let createdRef = null;
  let createdTransactionId = null;
  let createdSpendingData = null;
  let result = {
    variationId,
    passed: false,
    error: null,
    comparisonResult: null,
  };

  try {
    switch (variationId) {
      case 'S1': {
        // Create spending
        const timestamp = Date.now();
        const amount = 100 + Math.floor(Math.random() * 100);

        const spendingData = {
          organizationId,
          store: store.id,
          details: `Test spending ${timestamp}`,
          amount,
          category: { id: 'test', name: 'Test Category' },
          source: paymentSource,
          date: new Date(),
          dailyStatID: `test-spending-${timestamp}-${store.id}`,
        };

        log(`Creating spending: "${spendingData.details}", amount ${amount}`);
        ({ ref: createdRef, transactionId: createdTransactionId } = await saveNewSpending(db, spendingData));
        // ATL-1790: the copy deleteSpending is given must name the leg, or the leg is left behind.
        createdSpendingData = { ...spendingData, ref: createdRef, transactionId: createdTransactionId };

        log('Waiting 6000ms for Supabase sync...');
        await new Promise(resolve => setTimeout(resolve, 6000));

        // Compare
        const comparison = await compareSpendingViaApi(createdRef.id, spendingData, log);
        result.comparisonResult = comparison;
        result.passed = comparison.foundInSupabase;

        // Cleanup
        log('Cleaning up test spending...');
        await deleteSpending(db, createdRef, createdSpendingData);
        await new Promise(resolve => setTimeout(resolve, 2000));

        break;
      }

      case 'S2': {
        // Delete spending test
        const timestamp = Date.now();
        const amount = 50 + Math.floor(Math.random() * 50);

        const spendingData = {
          organizationId,
          store: store.id,
          details: `Test spending delete ${timestamp}`,
          amount,
          category: { id: 'test', name: 'Test Category' },
          source: paymentSource,
          date: new Date(),
          dailyStatID: `test-spending-del-${timestamp}-${store.id}`,
        };

        log('Creating spending to delete...');
        ({ ref: createdRef, transactionId: createdTransactionId } = await saveNewSpending(db, spendingData));
        // ATL-1790: the copy deleteSpending is given must name the leg, or the leg is left behind.
        createdSpendingData = { ...spendingData, ref: createdRef, transactionId: createdTransactionId };

        log('Waiting 6000ms for Supabase sync...');
        await new Promise(resolve => setTimeout(resolve, 6000));

        // Verify it exists first
        const createCheck = await compareSpendingViaApi(createdRef.id, spendingData, log);
        if (!createCheck.foundInSupabase) {
          result.error = 'Spending was not created in Supabase';
          break;
        }

        // Now delete
        log('Deleting spending...');
        await deleteSpending(db, createdRef, createdSpendingData);

        log('Waiting 6000ms for delete sync...');
        await new Promise(resolve => setTimeout(resolve, 6000));

        // Check if deleted
        const deleted = await checkSpendingDeleted(createdRef.id);
        result.passed = deleted;
        result.comparisonResult = {
          spendingId: createdRef.id,
          foundInSupabase: !deleted,
          matches: deleted,
          differences: deleted ? [] : [{
            field: 'deleted',
            supabaseValue: 'still exists',
            firestoreValue: 'deleted',
            severity: 'critical',
          }],
        };

        break;
      }

      default:
        result.error = `Unknown variation: ${variationId}`;
    }
  } catch (error) {
    result.error = error.message;
    log(`Error: ${error.message}`);

    // Cleanup on error
    if (createdSpendingData && createdRef) {
      try {
        await deleteSpending(db, createdRef, createdSpendingData);
      } catch (cleanupError) {
        log(`Cleanup failed: ${cleanupError.message}`);
      }
    }
  }

  return result;
}

/**
 * Run all spending tests
 */
export async function runFullSpendingTest(log = console.log, selectedVariations = null) {
  log('Starting spendings dual-write tests...');

  const testData = getSpendingTestData();
  if (!testData) {
    log('ERROR: Could not get test data. Need at least 1 store and to be logged in.');
    return { passed: 0, failed: 0, skipped: 0, results: [] };
  }

  log('\n========================================');
  log('SPENDINGS DUAL-WRITE TEST SUITE');
  log('========================================');
  log(`Organization: ${testData.organization.name || testData.organizationId}`);
  log(`Store: ${testData.store.name || testData.store.id}`);
  log(`Payment Source: ${testData.paymentSource.name || testData.paymentSource.id}`);
  log('========================================\n');

  const variationsToRun = selectedVariations || SPENDING_TEST_VARIATIONS.map(v => v.id);
  const results = [];
  let passed = 0;
  let failed = 0;
  let skipped = 0;

  for (const variationId of variationsToRun) {
    const variation = SPENDING_TEST_VARIATIONS.find(v => v.id === variationId);
    if (!variation) {
      log(`Skipping unknown variation: ${variationId}`);
      skipped++;
      continue;
    }

    log(`\n--- Running test ${variationId}: ${variation.name} ---`);

    const result = await runSingleTest(variationId, testData, log);
    results.push(result);

    if (result.error) {
      log(`ERROR: ${result.error}`);
      failed++;
    } else if (result.passed) {
      log(`PASS: ${variation.name}`);
      passed++;
    } else {
      log(`FAIL: ${variation.name}`);
      if (result.comparisonResult?.differences?.length > 0) {
        log('Differences: ' + JSON.stringify(result.comparisonResult.differences, null, 2));
      }
      failed++;
    }
  }

  log('\n========================================');
  log('TEST SUMMARY');
  log('========================================');
  log(`Passed: ${passed}`);
  log(`Failed: ${failed}`);
  log(`Skipped: ${skipped}`);
  log('========================================');

  if (failed > 0) {
    log('\nFAILED TESTS:');
    results.filter(r => !r.passed && !r.error).forEach(r => {
      const variation = SPENDING_TEST_VARIATIONS.find(v => v.id === r.variationId);
      log(`  - ${r.variationId}: ${variation?.name}`);
      r.comparisonResult?.differences?.forEach(d => {
        log(`    ${d.field}: Supabase="${d.supabaseValue}" vs Firestore="${d.firestoreValue}"`);
      });
    });
  }

  return { passed, failed, skipped, results };
}

export default {
  SPENDING_TEST_VARIATIONS,
  getSpendingTestCategories,
  getSpendingTestData,
  runFullSpendingTest,
};
